import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, AlertTriangle, Check, Trash2, ChevronDown, ChevronRight, Hash, Building2 } from 'lucide-react';
import { clientService } from '../../services/clientService';
import ConfirmDialog from '../common/ConfirmDialog';
import './FxgoCorrectionsModal.css';

interface CorrectionDiffEntry {
  field: string;
  oldValue: string | number | null;
  newValue: string | number | null;
}

interface PendingCorrection {
  id: string;
  fxgoTradeId: string;
  originalTradeId: string;
  originalTradeNumber: string | number;
  counterpartyName: string;
  currentWorkflowState: string;
  diff: CorrectionDiffEntry[];
  createdAt: string;
}

interface FxgoCorrectionsModalProps {
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
  onRefreshNeeded?: () => void;
}

const FxgoCorrectionsModal: React.FC<FxgoCorrectionsModalProps> = ({
  clientId,
  isOpen,
  onClose,
  onRefreshNeeded,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [corrections, setCorrections] = useState<PendingCorrection[]>([]);

  useEffect(() => {
    if (isOpen && clientId) {
      loadCorrections();
    }
  }, [isOpen, clientId]);

  const loadCorrections = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await clientService.getFxgoPendingCorrections(clientId);
      if (result.success && result.data) {
        setCorrections(result.data.corrections || []);
      } else {
        setError(result.message || t('fxgoCorrections.loadError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('fxgoCorrections.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (correctionId: string): Promise<boolean> => {
    const result = await clientService.acceptFxgoCorrection(clientId, correctionId);
    if (result.success) {
      setCorrections(prev => prev.filter(c => c.id !== correctionId));
      onRefreshNeeded?.();
    }
    return result.success;
  };

  const handleDismiss = async (correctionId: string): Promise<boolean> => {
    const result = await clientService.dismissFxgoCorrection(clientId, correctionId);
    if (result.success) {
      setCorrections(prev => prev.filter(c => c.id !== correctionId));
    }
    return result.success;
  };

  if (!isOpen) return null;

  return (
    <div className="fxgo-corrections-overlay" onClick={onClose}>
      <div className="fxgo-corrections-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="fxgo-corrections-header">
          <div className="header-title-area">
            <AlertTriangle size={22} className="header-icon" />
            <h2>{t('fxgoCorrections.title')}</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="fxgo-corrections-content">
          {loading ? (
            <div className="fxgo-corrections-loading">
              <Loader2 size={40} className="spinner-icon" />
              <p>{t('common.loading')}</p>
            </div>
          ) : error ? (
            <div className="fxgo-corrections-error">
              <AlertTriangle size={40} />
              <p>{error}</p>
              <button onClick={loadCorrections}>{t('common.retry')}</button>
            </div>
          ) : corrections.length === 0 ? (
            <div className="fxgo-corrections-empty">
              <Check size={56} strokeWidth={1} />
              <p>{t('fxgoCorrections.noCorrections')}</p>
            </div>
          ) : (
            <div className="fxgo-corrections-list">
              {corrections.map((correction) => (
                <CorrectionCard
                  key={correction.id}
                  correction={correction}
                  onAccept={handleAccept}
                  onDismiss={handleDismiss}
                  onCloseModal={onClose}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Individual correction card
const CorrectionCard: React.FC<{
  correction: PendingCorrection;
  onAccept: (correctionId: string) => Promise<boolean>;
  onDismiss: (correctionId: string) => Promise<boolean>;
  onCloseModal: () => void;
}> = ({ correction, onAccept, onDismiss, onCloseModal }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);

  const isBooked = correction.currentWorkflowState === 'Booked';
  const isBusy = accepting || dismissing;
  const changedFieldsCount = correction.diff.length;

  const handleAccept = async () => {
    setAccepting(true);
    await onAccept(correction.id);
    setAccepting(false);
  };

  const handleDismissConfirmed = async () => {
    setShowDismissConfirm(false);
    setDismissing(true);
    await onDismiss(correction.id);
    setDismissing(false);
  };

  return (
    <div className={`correction-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Clickable Header */}
      <div className="correction-card-header clickable" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <span className="expand-icon">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <div className="correction-info-compact">
            <span className="correction-trade-number">
              <Hash size={14} />
              {correction.originalTradeNumber}
            </span>
            <span className="correction-counterparty">
              <Building2 size={14} />
              {correction.counterpartyName}
            </span>
            <span className="correction-fxgo-id">
              FXGO: {correction.fxgoTradeId}
            </span>
            <span className={`correction-state-badge ${correction.currentWorkflowState.toLowerCase()}`}>
              {correction.currentWorkflowState}
            </span>
            {!isExpanded && (
              <span className="correction-fields-badge">
                {t('fxgoCorrections.changedFields', { count: changedFieldsCount })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <>
          {/* Diff Table */}
          <div className="correction-diff-section">
            <table className="correction-diff-table">
              <thead>
                <tr>
                  <th>{t('fxgoCorrections.fieldName')}</th>
                  <th>{t('fxgoCorrections.oldValue')}</th>
                  <th>{t('fxgoCorrections.newValue')}</th>
                </tr>
              </thead>
              <tbody>
                {correction.diff.map((entry, idx) => (
                  <tr key={idx}>
                    <td className="diff-field-name">
                      {t(`fxgoCorrections.fields.${entry.field}`, { defaultValue: entry.field })}
                    </td>
                    <td className="diff-old-value">{entry.oldValue ?? '-'}</td>
                    <td className="diff-new-value">{entry.newValue ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Warning if not Booked */}
          {!isBooked && (
            <div className="correction-warning">
              <AlertTriangle size={16} />
              <span>{t('fxgoCorrections.mustBeBooked', { state: correction.currentWorkflowState })}</span>
            </div>
          )}

          {/* Action Footer */}
          <div className="correction-card-footer">
            <button
              className="dismiss-btn"
              onClick={(e) => { e.stopPropagation(); setShowDismissConfirm(true); }}
              disabled={isBusy}
            >
              {dismissing ? (
                <Loader2 size={16} className="spinner-icon" />
              ) : (
                <Trash2 size={16} />
              )}
              <span>{t('fxgoCorrections.dismiss')}</span>
            </button>
            {isBooked ? (
              <button
                className="accept-btn"
                onClick={(e) => { e.stopPropagation(); handleAccept(); }}
                disabled={isBusy}
              >
                {accepting ? (
                  <Loader2 size={16} className="spinner-icon" />
                ) : (
                  <Check size={16} />
                )}
                <span>{t('fxgoCorrections.accept')}</span>
              </button>
            ) : (
              <button
                className="close-modal-btn"
                onClick={(e) => { e.stopPropagation(); onCloseModal(); }}
              >
                <X size={16} />
                <span>{t('common.close')}</span>
              </button>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={showDismissConfirm}
        title={t('fxgoCorrections.dismissConfirmTitle')}
        message={t('fxgoCorrections.dismissConfirmMessage')}
        confirmLabel={t('fxgoCorrections.dismiss')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleDismissConfirmed}
        onCancel={() => setShowDismissConfirm(false)}
        type="warning"
      />
    </div>
  );
};

export default FxgoCorrectionsModal;
